import { useFormContext } from 'react-hook-form';
import {
    FormInputField,
    FormSelectField,
    FormSelectionCardGroupField,
    FormTextareaField,
    LocalizedText,
    lt,
    useFeatureFlag,
} from '@mews/framework';
import { Icon, IconName, DropdownItem, Title } from '@optimus-web/core';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { LocalizedSmallParagraph, ResponsiveTwoColumns } from '@mews-commander/ui';
import { HourSelectField } from '@mews-commander/form';
import { RoutingRuleTriggerType } from '@mews-commander/data-access';
import { compactArray } from '@mews/fp';
import { RoutingRulesFormFields } from '../../utils';
import { useRoutingRuleIdContext } from '../../../../contexts/RoutingRuleIdContext';
import { consumptionDateOptions, dayOfMonthOptions } from './constants';

export const getTriggerOptions = (recurringRoutingRulesEnabled: boolean) =>
    compactArray([
        {
            media: <Icon value={IconName.Refresh} variant="basic" />,
            label: <LocalizedText textKey={TextKey.RoutingRulesFormTriggerLabelContinuous} />,
            value: RoutingRuleTriggerType.Continuous,
            name: RoutingRuleTriggerType.Continuous,
            description: <LocalizedText textKey={TextKey.RoutingRulesFormTriggerDescriptionContinuous} />,
        },
        recurringRoutingRulesEnabled
            ? {
                  media: <Icon value={IconName.CalendarDay} variant="basic" />,
                  label: <LocalizedText textKey={TextKey.RoutingRulesFormTriggerLabelMonthly} />,
                  value: RoutingRuleTriggerType.Recurring,
                  name: RoutingRuleTriggerType.Recurring,
                  description: <LocalizedText textKey={TextKey.RoutingRulesFormTriggerDescriptionMonthly} />,
              }
            : null,
    ]);

export const RoutingRuleFormTimingFields = () => {
    const { watch } = useFormContext();
    const triggerTimingValue = watch(RoutingRulesFormFields.triggerTiming());
    const recurringRoutingRulesEnabled = useFeatureFlag(FeatureFlag.recurringRoutingRulesEnabled);
    const { routingRuleId } = useRoutingRuleIdContext();

    return (
        <>
            <FormInputField name={RoutingRulesFormFields.name()} required label={lt('Name')} />
            <FormTextareaField rows={1} name={RoutingRulesFormFields.description()} label={lt('Description')} />
            <Title level={2}>
                <LocalizedText textKey={TextKey.RoutingRulesFormFieldTitleTiming} />
            </Title>
            <LocalizedSmallParagraph textKey={TextKey.RoutingRulesFormFieldDescriptionTrigger} variant="secondary" />
            <FormSelectionCardGroupField
                type="radio"
                name={RoutingRulesFormFields.triggerTiming()}
                options={getTriggerOptions(recurringRoutingRulesEnabled)}
                disabled={routingRuleId != null}
            />
            {recurringRoutingRulesEnabled && triggerTimingValue === RoutingRuleTriggerType.Recurring && (
                <>
                    <ResponsiveTwoColumns>
                        <FormSelectField
                            name={RoutingRulesFormFields.triggerDay()}
                            label={<LocalizedText textKey={TextKey.RoutingRulesFormDayOfMonthLabel} />}
                        >
                            {dayOfMonthOptions.map((day) => (
                                <DropdownItem key={day.key} value={day.value} label={day.primaryText} />
                            ))}
                        </FormSelectField>
                        <HourSelectField
                            showEmptyValue={false}
                            name={RoutingRulesFormFields.triggerTime()}
                            label={<LocalizedText textKey={TextKey.RoutingRulesFormTimeOfMonthLabel} />}
                        />
                    </ResponsiveTwoColumns>
                    <FormSelectField
                        name={RoutingRulesFormFields.consumptionDate()}
                        label={<LocalizedText textKey={TextKey.RoutingRulesFormConsumptionDateLabel} />}
                    >
                        {consumptionDateOptions.map((consumptionDate) => (
                            <DropdownItem
                                key={consumptionDate.key}
                                value={consumptionDate.value}
                                label={<LocalizedText textKey={consumptionDate.primaryText} />}
                                description={<LocalizedText textKey={consumptionDate.secondaryText} />}
                            />
                        ))}
                    </FormSelectField>
                </>
            )}
        </>
    );
};
